import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, shareReplay } from "rxjs";
import { environment } from "../config/environment";
import { AdminConfigKey, ClientAdminConfigValueDto, DivisionClientAdminConfigDto, TypedAdminConfigKey } from "./models/AdminConfigDto";
import { LoggerService } from "../../shared/services/logger.service";

export interface FeatureResponse {
  [key: string]: boolean | DivisionClientAdminConfigDto[];
  divisionConfigForUser: DivisionClientAdminConfigDto[];
}

export enum FeatureFlag {
  EnableBQ6 = "bqA_EnableBQ6",
  EnableBQ9 = "bqA_EnableBQ9",
  EnableBQ234 = "bqA_EnableBQ234",
  EnableBQ15 = "bqA_EnableBQ15"
}

@Injectable({
  providedIn: "root"
})
export class FeatureService {
  private readonly url = `${environment.backend.apiBaseURL}/api/Features`;
  private features$: Observable<FeatureResponse> | null = null;

  constructor(private http: HttpClient, private logger: LoggerService) { }

  getFeatures(): Observable<FeatureResponse> {
    if (!this.features$) {
      this.features$ = this.http.get<FeatureResponse>(this.url).pipe(
        shareReplay(1)
      );
    }
    return this.features$;
  }

  isEnabled(flagName: string): Observable<boolean> {
    return new Observable<boolean>(subscriber => {
      this.getFeatures().subscribe({
        next: flags => subscriber.next(flags[flagName] === true),
        error: () => subscriber.next(false),
        complete: () => subscriber.complete()
      });
    });
  }

  getConfigForDivision<T extends AdminConfigKey>(configKey: T, divisionId: number): Promise<ClientAdminConfigValueDto<T>> {
    return new Promise<ClientAdminConfigValueDto<T>>((resolve, reject) => {
      this.getFeatures().subscribe({
        next: flags => {
          const divisionConfig = flags.divisionConfigForUser.find(d => d.id === divisionId);
          if (divisionConfig && divisionConfig.configs[configKey]) {
            resolve({
              value: divisionConfig.configs[configKey].value as TypedAdminConfigKey<T>,
              defaultValue: divisionConfig.configs[configKey].defaultValue as TypedAdminConfigKey<T>
            });
          } else {
            reject(new Error(`Config for key '${configKey}' not found for division '${divisionId}'.`));
          }
        },
        error: err => reject(err)
      });
    });
  }

  getEffectiveConfig<T extends AdminConfigKey>(configKey: T): Promise<TypedAdminConfigKey<T>> {
    return new Promise<TypedAdminConfigKey<T>>((resolve, reject) => {
      this.getFeatures().subscribe({
        next: flags => {
          const configs = flags?.divisionConfigForUser?.map(d => d.configs[configKey]) ?? [];

          if (configs.length === 0 || configs.some(c => !c)) {
            this.logger.warn(`Config for key '${configKey}' not found.`);
            resolve(null as TypedAdminConfigKey<T>);
          } else {
            const [firstConfig] = configs;
            const hasConsensus = configs.every(c => c.value === firstConfig.value);

            const effectiveConfig: TypedAdminConfigKey<T> = hasConsensus
              ? firstConfig.value as TypedAdminConfigKey<T>
              : firstConfig.defaultValue as TypedAdminConfigKey<T>;

            resolve(effectiveConfig);
          }
        },
        error: err => reject(err)
      });
    });
  }
}
